import { SelectHTMLAttributes, forwardRef, useState, useRef, useEffect, useLayoutEffect, useMemo, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Pencil } from 'lucide-react';
import { FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { cn } from '../../lib/utils';
import { authenticatedFetch } from '../../lib/api';
import { ResourceManager } from '../ResourceManager/ResourceManager';
import { SearchTextInput } from './SearchTextInput';
import { useFloatingPosition } from '../../Hooks/useFloatingPosition';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
    label?: string;
    labelClassName?: string;
    options: SelectOption[];
    placeholder?: string;
    error?: string | { message?: string } | null;
    helper?: string;
    fullWidth?: boolean;
    crudEndpoint?: string;
    entityName?: string;
    onManageClose?: () => void;
    manageColumns?: any[];
    manageSize?: 'sm' | 'md' | 'lg' | 'xl';
    onValueChange?: (value: string | number, label?: string) => void;
    variant?: 'primary' | 'inline';
    quickAdd?: boolean;
    quickAddField?: string;
    /** Shows a required marker next to the label */
    required?: boolean;
}

/**
 * Select — Unified accessible dropdown. Replaces all native <select> usage.
 * Compatible with React Hook Form via `register()`.
 *
 * @example
 * <Select label="Class" options={classOptions} {...register('classroomId')} error={errors.classroomId} />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
    ({
        label,
        labelClassName,
        options,
        placeholder,
        error,
        helper,
        fullWidth = true,
        className,
        id,
        onValueChange,
        variant = 'primary',
        crudEndpoint,
        entityName,
        onManageClose,
        manageColumns,
        manageSize,
        quickAdd,
        quickAddField,
        required,
        ...rest
    }, ref) => {
        const [newlyAddedOptions, setNewlyAddedOptions] = useState<SelectOption[]>([]);
        const [isManageOpen, setIsManageOpen] = useState(false);
        const [isQuickAdding, setIsQuickAdding] = useState(false);
        const [open, setOpen] = useState(false);
        const [searchTerm, setSearchTerm] = useState(''); // For immediate input display
        const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // For filtering the list
        const selectRef = useRef<HTMLSelectElement>(null);
        const searchInputRef = useRef<HTMLInputElement>(null);
        const dropdownRef = useRef<HTMLDivElement>(null);
        const triggerRef = useRef<HTMLDivElement>(null);

        const { coords } = useFloatingPosition(triggerRef, dropdownRef, open, { maxHeight: 400 });

        useEffect(() => {
            if (open) {
                const timer = setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 60);
                return () => clearTimeout(timer);
            } else {
                setSearchTerm('');
                setDebouncedSearchTerm('');
            }
        }, [open]);

        // Close on outside click or Escape
        useEffect(() => {
            const handler = (e: MouseEvent) => {
                const target = e.target as Node;
                const clickedInsideTrigger = triggerRef.current?.contains(target);
                const clickedInsideDropdown = dropdownRef.current?.contains(target);

                if (!clickedInsideTrigger && !clickedInsideDropdown) {
                    setOpen(false);
                }
            };
            const keyHandler = (e: KeyboardEvent) => {
                if (e.key === 'Escape' && open) {
                    e.stopPropagation();
                    setOpen(false);
                }
            };
            if (open) {
                document.addEventListener('mousedown', handler);
                document.addEventListener('keydown', keyHandler);
            }
            return () => {
                document.removeEventListener('mousedown', handler);
                document.removeEventListener('keydown', keyHandler);
            };
        }, [open]);

        // Merge refs: expose the internal select ref
        useImperativeHandle(ref, () => selectRef.current as HTMLSelectElement);

        const errorMsg = typeof error === 'string' ? error : (error as any)?.message;
        const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

        const actualOptions = useMemo(() => {
            const base = options || [];
            // Merge newly added options, avoiding duplicates
            const filteredNew = newlyAddedOptions.filter(no => !base.some(bo => String(bo.value) === String(no.value)));
            return [...base, ...filteredNew];
        }, [options, newlyAddedOptions]);

        const renderedOptions = useMemo(() => {
            if (!placeholder) {
                return actualOptions;
            }

            return actualOptions.filter(opt => String(opt.value) !== '');
        }, [actualOptions, placeholder]);

        const isSearchable = renderedOptions.length > 5;

        // Sync internal value for display when searchable
        const [internalValue, setInternalValue] = useState<any>(rest.value || rest.defaultValue || '');

        const hasMatchingOption = useMemo(() =>
            renderedOptions.some(opt => String(opt.value) === String(internalValue)),
            [renderedOptions, internalValue]
        );

        useEffect(() => {
            if (rest.value !== undefined) {
                setInternalValue(rest.value as string | number);
            }
        }, [rest.value]);

        // Tracks whether we've already notified the parent for the current uncontrolled phase.
        // Resets when the parent takes control (rest.value becomes defined).
        const autoSyncedRef = useRef(false);
        useEffect(() => {
            if (rest.value !== undefined) autoSyncedRef.current = false;
        }, [rest.value]);

        // When options first arrive while uncontrolled (no value prop, no placeholder), fire
        // onChange so the parent form learns which value the browser is showing. Skip options
        // with a falsy value (e.g. a "None" sentinel) — those aren't real selections.
        // Tags the event _autoSelected so handlers can distinguish this from a deliberate pick.
        useEffect(() => {
            if (rest.value !== undefined || placeholder || !renderedOptions.length || autoSyncedRef.current) return;
            const firstValue = renderedOptions[0].value;
            if (!firstValue) return;
            autoSyncedRef.current = true;
            setInternalValue(firstValue);
            rest.onChange?.({
                target: { name: rest.name, value: firstValue, _autoSelected: true },
                currentTarget: { name: rest.name, value: firstValue },
                persist: () => {},
                preventDefault: () => {},
                stopPropagation: () => {},
            } as any);
            // autoSyncedRef gates the actual call; a stale onChange closure from an
            // inline arrow function only costs one missed auto-sync on pathological
            // re-mount scenarios — acceptable given the guard prevents double-fire.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [renderedOptions]);

        // When no value prop is provided (RHF uncontrolled via register()), sync internalValue
        // from the DOM element's value, which RHF's ref callback sets during commit — before
        // layout effects run but after React's own DOM mutations. This ensures the Select
        // displays the correct default/reset value when it first mounts.
        useLayoutEffect(() => {
            if (rest.value === undefined && selectRef.current?.value) {
                setInternalValue(selectRef.current.value);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

        const filteredOptions = useMemo(() =>
            renderedOptions.filter(opt =>
                (opt.label || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            ),
            [renderedOptions, debouncedSearchTerm]);

        const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setSearchTerm(val);

            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

            searchTimeoutRef.current = setTimeout(() => {
                setDebouncedSearchTerm(val);
            }, 250);
        };

        const handleSelectClick = (_e?: any) => {
            if (!rest.disabled && isSearchable) {
                setOpen(!open);
            }
        };

        const handleOptionClick = (val: string | number, optLabel?: string) => {
            const resolvedLabel = optLabel || actualOptions.find(opt => String(opt.value) === String(val))?.label || '';
            setInternalValue(val);
            setOpen(false);
            setSearchTerm('');

            if (onValueChange) {
                onValueChange(val, resolvedLabel);
            }

            if (rest.onChange) {
                // Dispatch a synthetic change event for RHF/native listeners
                const event = {
                    target: {
                        name: rest.name,
                        value: val,
                        label: resolvedLabel,
                    },
                    currentTarget: {
                        name: rest.name,
                        value: val,
                    },
                    persist: () => {},
                    preventDefault: () => {},
                    stopPropagation: () => {},
                } as any;
                rest.onChange(event);
            }
        };

        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            const val = e.target.value;
            const option = actualOptions.find(opt => String(opt.value) === String(val));
            const label = option?.label || '';

            setInternalValue(val);
            if (onValueChange) {
                onValueChange(option ? option.value : val, label);
            }
            if (rest.onChange) {
                (e.target as any).label = label;
                rest.onChange(e);
            }
        };

        const handleQuickAdd = async () => {
            if (!crudEndpoint || !searchTerm || isQuickAdding) return;

            setIsQuickAdding(true);
            try {
                const fieldName = quickAddField || 'displayName';
                const payload = { [fieldName]: searchTerm };
                const res = await authenticatedFetch(crudEndpoint, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });

                const newId = res?.id || res?.data?.id || res?.value || res?.data?.value;

                const addedName = searchTerm;
                toast.success(`"${addedName}" added successfully`);
                setSearchTerm('');
                setOpen(false);
                // Trigger refetch in parent
                onManageClose?.();

                // Optimistically add to options and select
                if (newId) {
                    const newOption = { value: newId, label: addedName };
                    setNewlyAddedOptions(prev => [...prev, newOption]);
                    handleOptionClick(newId, addedName);
                }
            } catch (e: any) {
                const errorsFirst = Array.isArray(e?.errors) && e.errors.length > 0 ? String(e.errors[0]) : undefined;
                toast.error(errorsFirst || e?.detail || e?.message || 'Failed to add item');
            } finally {
                setIsQuickAdding(false);
            }
        };

        const isInline = variant === 'inline';

        return (
            <div className={cn('flex flex-col gap-1.5 relative', fullWidth && !isInline && 'w-full', isInline && 'inline-flex')}>
                {label && (
                    <label htmlFor={inputId} className={cn("text-xs font-bold text-slate-600 select-none", labelClassName)}>
                        {label}
                        {required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                )}
                <div className="relative flex items-center">
                    <div
                        ref={triggerRef}
                        onClick={handleSelectClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSelectClick(e as any);
                            }
                        }}
                        tabIndex={isSearchable && !rest.disabled ? 0 : -1}
                        className={cn(
                            "relative flex items-center w-full rounded-xl transition-all duration-150",
                            !isInline && "bg-white border focus-within:ring-4 focus-within:ring-teal-primary/20 focus-within:border-teal-primary",
                            !isInline && (errorMsg ? 'border-red-400' : 'border-slate-200'),
                            isSearchable && !rest.disabled && "cursor-pointer",
                            rest.disabled && "bg-slate-50 opacity-60 cursor-not-allowed"
                        )}
                    >
                        <select
                            ref={selectRef}
                            id={inputId}
                            aria-label={label || placeholder}
                            className={cn(
                                !isInline && 'w-full h-10 pl-3 bg-transparent text-sm text-slate-900 appearance-none min-w-[140px]',
                                !isInline && (crudEndpoint ? 'pr-16' : 'pr-9'),
                                !isInline && 'outline-none cursor-pointer border-none',
                                isInline && 'bg-transparent cursor-pointer outline-none appearance-none pr-5',
                                isSearchable && "pointer-events-none", // Let the div handle clicks when searchable
                                className
                            )}
                            {...rest}
                            required={required}
                            value={internalValue}
                            onChange={handleChange}
                        >
                            {placeholder && (
                                <option value="">{placeholder}</option>
                            )}
                            {!placeholder && !hasMatchingOption && (
                                <option value={internalValue} hidden />
                            )}
                            {renderedOptions.map((opt) => (
                                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <div className={cn(
                            "absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1",
                            isInline && "right-0"
                        )}>
                            {crudEndpoint && !rest.disabled && !isInline && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsManageOpen(true);
                                    }}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-teal-600 hover:bg-teal-50 hover:text-teal-700 transition-all active:scale-95"
                                    title={`Manage ${entityName || label || 'Items'}`}
                                >
                                    <FiPlus size={16} />
                                </button>
                            )}
                            <ChevronDown
                                size={isInline ? 12 : 14}
                                className="text-slate-400 pointer-events-none shrink-0"
                            />
                        </div>
                    </div>
                </div>

                    {open && coords && createPortal(
                        <div
                            ref={dropdownRef}
                            className={cn(
                                "fixed z-[30000] animate-in fade-in duration-150",
                                coords?.isFlipped ? "slide-in-from-bottom-2" : "slide-in-from-top-2"
                            )}
                            style={{
                                top: coords?.isFlipped ? undefined : (coords?.top || 0) + 4,
                                bottom: coords?.isFlipped ? (coords?.bottom || 0) + 4 : undefined,
                                left: coords?.left || 0,
                                width: coords?.width || 0,
                            }}
                        >
                            <div className="backdrop-blur-2xl bg-white/80 border border-white/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[400px]">
                                <div className="p-2 border-b border-slate-50 bg-slate-50/30">
                                    <SearchTextInput
                                        ref={searchInputRef}
                                        placeholder={placeholder || "Search..."}
                                        onChange={handleSearchChange}
                                        value={searchTerm}
                                        autoFocus
                                    />
                                </div>
                                <div className="overflow-y-auto py-1 custom-scrollbar">
                                    {filteredOptions.length === 0 ? (
                                        <div className="p-2 space-y-2">
                                            <div className="px-4 py-8 text-center text-[10px] text-slate-400 uppercase font-black tracking-widest">
                                                No results found
                                            </div>
                                            {searchTerm && crudEndpoint && (
                                                <button
                                                    type="button"
                                                    onClick={quickAdd ? handleQuickAdd : () => setIsManageOpen(true)}
                                                    disabled={quickAdd && isQuickAdding}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-[10px] font-black uppercase tracking-wider text-teal-600 bg-teal-50/50 hover:bg-teal-50 rounded-xl border border-dashed border-teal-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <FiPlus size={14} />
                                                    {quickAdd && isQuickAdding ? 'Adding...' : `Add "${searchTerm}"`}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        filteredOptions.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => handleOptionClick(opt.value, opt.label)}
                                                className={cn(
                                                    "w-full text-left px-4 py-2.5 text-sm font-semibold transition-all duration-200",
                                                    String(opt.value) === String(internalValue)
                                                        ? "bg-teal-50 text-teal-700"
                                                        : "text-slate-700 hover:bg-slate-50 hover:pl-6 hover:text-teal-600"
                                                )}
                                            >
                                                {opt.label}
                                            </button>
                                        ))
                                    )}
                                </div>
                                {crudEndpoint && (
                                    <div className="flex justify-start p-2 border-t border-slate-50 bg-slate-50/30">
                                        <button
                                            type="button"
                                            onClick={() => setIsManageOpen(true)}
                                            className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-teal-600 hover:text-teal-700 transition-colors"
                                        >
                                            <Pencil size={10} />
                                            + Add / Manage {entityName || label || 'Items'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>,
                        document.body
                    )}



                {isManageOpen && crudEndpoint && (
                    <ResourceManager
                        isOpen={isManageOpen}
                        onClose={() => {
                            setIsManageOpen(false);
                            onManageClose?.();
                        }}
                        title={`Manage ${entityName || label || 'Items'}`}
                        endpoint={crudEndpoint}
                        columns={manageColumns}
                        size={manageSize}
                        defaultRow={searchTerm ? { [quickAddField || 'displayName']: searchTerm } : undefined}
                        autoAdd={!!searchTerm}
                    />
                )}
                {errorMsg && (
                    <p className="text-xs font-medium text-red-500">{errorMsg}</p>
                )}
                {!errorMsg && helper && (
                    <p className="text-xs text-slate-400">{helper}</p>
                )}
            </div>
        );
    }
);

Select.displayName = 'Select';
